import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  alpha,
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  Link,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  AutoAwesome as SparkleIcon,
  Person as PersonIcon,
  RestartAlt as RestartIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePageTitle } from '../utils/pageTitle';
import { ApiError } from '../services/learningApi';
import {
  buildGuideApi,
  BuildGuideAction,
  BuildGuideRecommendation,
  BuildGuideRecommendationKind,
  BuildGuideTurn,
  MAX_MESSAGE_CHARS,
} from '../services/buildGuideApi';

interface GuideMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  quickReplies?: string[];
  actions?: BuildGuideAction[];
  recommendation?: BuildGuideRecommendation | null;
}

interface GuideError {
  message: string;
  status?: number;
}

const STORAGE_PREFIX = 'buildGuide:v1:';
const MAX_STORED_MESSAGES = 100;

const STARTERS: { label: string; message: string }[] = [
  {
    label: 'Extract data from receipts or emails',
    message: 'I want to pull information like names, dates and totals out of receipts or emails automatically.',
  },
  {
    label: 'A support chatbot that knows my docs',
    message: 'I want a customer support chatbot that answers questions using my own documents.',
  },
  {
    label: 'Turn product photos into listings',
    message: 'I want to turn product photos into ready-to-publish product listings.',
  },
  {
    label: 'A multi-step research pipeline',
    message: 'I want a multi-step pipeline that researches a topic, summarizes what it finds and writes a report.',
  },
  {
    label: 'Classify or tag content',
    message: 'I want to automatically classify or tag pieces of content, like tickets, reviews or articles.',
  },
  {
    label: "I'm not sure yet",
    message: "I'm not sure what to build yet. Can you help me figure out what EasyAI could do for me?",
  },
];

const KIND_LABELS: Record<BuildGuideRecommendationKind, string> = {
  function: 'AI function',
  chain: 'Chain',
  chatbot: 'Chatbot',
  studio: 'Studio app',
  other: 'Recommendation',
};

function newId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** crypto.randomUUID only exists in secure contexts (https or localhost). */
function newConversationId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function isInternalPath(path: string | undefined): path is string {
  return !!path && path.startsWith('/') && !path.startsWith('//') && !path.includes('\\');
}

function asStrings(value: unknown, max: number): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string').slice(0, max) : [];
}

function restoreMessages(parsed: unknown): GuideMessage[] {
  try {
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((m) => m && (m.role === 'user' || m.role === 'assistant') && typeof m.content === 'string')
      .slice(-MAX_STORED_MESSAGES)
      .map((m) => ({
        id: typeof m.id === 'string' ? m.id : newId(),
        role: m.role,
        content: m.content,
        quickReplies: asStrings(m.quickReplies, 4),
        actions: Array.isArray(m.actions)
          ? m.actions.filter((a: BuildGuideAction) => a && typeof a.label === 'string' && isInternalPath(a.path)).slice(0, 4)
          : [],
        recommendation:
          m.recommendation && typeof m.recommendation.summary === 'string' && m.recommendation.kind in KIND_LABELS
            ? m.recommendation
            : null,
      }));
  } catch {
    return [];
  }
}

function restoreConversation(raw: string | null): { conversationId: string; messages: GuideMessage[] } {
  let parsed: unknown = null;
  try {
    parsed = raw ? JSON.parse(raw) : null;
  } catch {
    parsed = null;
  }
  // Histories saved before conversation ids existed are a bare message array.
  const stored = (Array.isArray(parsed) ? { messages: parsed } : parsed && typeof parsed === 'object' ? parsed : {}) as {
    conversationId?: unknown;
    messages?: unknown;
  };
  const id = stored.conversationId;
  return {
    conversationId: typeof id === 'string' && UUID_RE.test(id) ? id : newConversationId(),
    messages: restoreMessages(stored.messages),
  };
}

/** What the guide sees of its own earlier turns: the reply plus what it recommended and linked. */
function toTurn(m: GuideMessage): BuildGuideTurn {
  if (m.role === 'user') return { role: 'user', content: m.content };
  const extras: string[] = [];
  if (m.recommendation) extras.push(`Recommended: ${KIND_LABELS[m.recommendation.kind]}: ${m.recommendation.summary}`);
  if (m.actions?.length) extras.push(`Links offered: ${m.actions.map((a) => a.label).join('; ')}`);
  return { role: 'assistant', content: extras.length ? `${m.content}\n\n${extras.join('\n')}` : m.content };
}

function errorFrom(e: unknown): GuideError {
  if (e instanceof ApiError) {
    if (e.status === 402) {
      return { status: 402, message: "You're out of AI credits. Top up or change your plan to keep chatting with the guide." };
    }
    if (e.status === 422) return { status: 422, message: e.message || 'That message could not be sent.' };
    return { status: e.status, message: e.message || 'The Build Guide is unavailable right now.' };
  }
  return { message: 'Could not reach the Build Guide. Check your connection and try again.' };
}

const markdownSx = {
  fontSize: '0.95rem',
  lineHeight: 1.6,
  wordBreak: 'break-word' as const,
  '& p': { m: 0, mb: 1.25 },
  '& > :last-child': { mb: 0 },
  '& ul, & ol': { m: 0, mb: 1.25, pl: 3 },
  '& li': { mb: 0.5 },
  '& h1, & h2, & h3, & h4': { fontSize: '1rem', fontWeight: 700, mt: 1.5, mb: 0.75 },
  '& code': {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: '0.85em',
    bgcolor: 'action.hover',
    px: 0.5,
    borderRadius: 0.5,
  },
  '& pre': {
    m: 0,
    mb: 1.25,
    p: 1.5,
    overflow: 'auto',
    borderRadius: 1,
    bgcolor: 'grey.900',
    color: 'grey.100',
    fontSize: '0.8rem',
    lineHeight: 1.5,
  },
  '& pre code': { bgcolor: 'transparent', p: 0, color: 'inherit', fontSize: 'inherit' },
  '& blockquote': { m: 0, mb: 1.25, pl: 1.5, borderLeft: '3px solid', borderColor: 'divider', color: 'text.secondary' },
  '& table': { borderCollapse: 'collapse', mb: 1.25, fontSize: '0.85rem', display: 'block', overflowX: 'auto' },
  '& th, & td': { border: '1px solid', borderColor: 'divider', px: 1, py: 0.5, textAlign: 'left' },
};

const TypingIndicator: React.FC = () => (
  <Box
    aria-label="The guide is typing"
    role="status"
    sx={{
      display: 'inline-flex',
      gap: 0.6,
      alignItems: 'center',
      px: 2,
      py: 1.5,
      '@keyframes buildGuideDot': {
        '0%, 80%, 100%': { opacity: 0.25, transform: 'translateY(0)' },
        '40%': { opacity: 1, transform: 'translateY(-3px)' },
      },
    }}
  >
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          bgcolor: 'text.secondary',
          animation: 'buildGuideDot 1.2s infinite ease-in-out',
          animationDelay: `${i * 0.15}s`,
        }}
      />
    ))}
  </Box>
);

const RecommendationCard: React.FC<{ recommendation: BuildGuideRecommendation }> = ({ recommendation }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      borderColor: (t) => alpha(t.palette.primary.main, 0.4),
      bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
      <Chip size="small" color="primary" label={KIND_LABELS[recommendation.kind] || KIND_LABELS.other} />
      <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1 }}>
        Recommended
      </Typography>
    </Stack>
    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: recommendation.why ? 0.5 : 0 }}>
      {recommendation.summary}
    </Typography>
    {recommendation.why && (
      <Typography variant="body2" color="text.secondary">
        {recommendation.why}
      </Typography>
    )}
  </Paper>
);

export const BuildGuide: React.FC = () => {
  usePageTitle('Build Guide');
  const navigate = useNavigate();
  const { getIdToken, getEffectiveCustomerId, permissions, user } = useAuth();
  const api = useMemo(() => buildGuideApi(getIdToken), [getIdToken]);

  const customerKey = getEffectiveCustomerId() || permissions?.customerId || user?.uid || '';
  const storageKey = customerKey ? `${STORAGE_PREFIX}${customerKey}` : null;

  const [messages, setMessages] = useState<GuideMessage[]>([]);
  const [conversationId, setConversationId] = useState<string>(newConversationId);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<GuideError | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!storageKey) return;
    abortRef.current?.abort();
    const restored = restoreConversation(localStorage.getItem(storageKey));
    setMessages(restored.messages);
    setConversationId(restored.conversationId);
    setError(null);
    setSending(false);
    setLoadedKey(storageKey);
  }, [storageKey]);

  useEffect(() => {
    if (!storageKey || loadedKey !== storageKey) return;
    try {
      if (messages.length) {
        localStorage.setItem(storageKey, JSON.stringify({ conversationId, messages: messages.slice(-MAX_STORED_MESSAGES) }));
      } else {
        localStorage.removeItem(storageKey);
      }
    } catch {
      /* storage full or unavailable: the chat still works for this session */
    }
  }, [messages, conversationId, storageKey, loadedKey]);

  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending, error]);

  useEffect(() => {
    if (!sending && loadedKey) inputRef.current?.focus();
  }, [sending, loadedKey]);

  const requestReply = useCallback(
    async (history: GuideMessage[], convId: string) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setSending(true);
      setError(null);
      try {
        const res = await api.chat(history.map(toTurn), convId, controller.signal);
        if (controller.signal.aborted) return;
        setMessages((prev) => [
          ...prev,
          {
            id: newId(),
            role: 'assistant',
            content: res.reply,
            quickReplies: res.quick_replies,
            actions: res.actions.filter((a) => isInternalPath(a.path)),
            recommendation: res.recommendation,
          },
        ]);
      } catch (e) {
        if (controller.signal.aborted) return;
        setError(errorFrom(e));
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
          setSending(false);
        }
      }
    },
    [api],
  );

  const send = useCallback(
    (text: string) => {
      const content = text.trim().slice(0, MAX_MESSAGE_CHARS);
      if (!content || sending) return;
      const next: GuideMessage[] = [...messages, { id: newId(), role: 'user', content }];
      setMessages(next);
      setInput('');
      void requestReply(next, conversationId);
    },
    [messages, sending, requestReply, conversationId],
  );

  // ?start=<idea> (from the dashboard) opens a fresh conversation with that message.
  const [searchParams, setSearchParams] = useSearchParams();
  const startParam = searchParams.get('start');
  const startedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!startParam || !loadedKey || startedRef.current === startParam) return;
    startedRef.current = startParam;
    const next = new URLSearchParams(searchParams);
    next.delete('start');
    setSearchParams(next, { replace: true });
    const content = startParam.trim().slice(0, MAX_MESSAGE_CHARS);
    if (!content) return;
    const first: GuideMessage[] = [{ id: newId(), role: 'user', content }];
    const convId = newConversationId();
    setConversationId(convId);
    setMessages(first);
    void requestReply(first, convId);
  }, [startParam, loadedKey, searchParams, setSearchParams, requestReply]);

  const retry = () => {
    if (!sending && messages.length && messages[messages.length - 1].role === 'user') void requestReply(messages, conversationId);
  };

  const startOver = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setSending(false);
    setError(null);
    setMessages([]);
    setConversationId(newConversationId());
    setInput('');
  };

  const trackClick = useCallback((path: string) => api.trackAction(conversationId, path), [api, conversationId]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
    e.preventDefault();
    send(input);
  };

  const markdownComponents = useMemo<React.ComponentProps<typeof ReactMarkdown>['components']>(
    () => ({
      a: ({ href, children }: { href?: string; children?: React.ReactNode }) =>
        isInternalPath(href) ? (
          <Link
            href={href}
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              trackClick(href);
              navigate(href);
            }}
          >
            {children}
          </Link>
        ) : href && /^https?:\/\//i.test(href) ? (
          <Link href={href} target="_blank" rel="noopener noreferrer">
            {children}
          </Link>
        ) : (
          <>{children}</>
        ),
      img: ({ alt }: { alt?: string }) => (alt ? <>{alt}</> : null),
    }),
    [navigate, trackClick],
  );

  const lastMessage = messages[messages.length - 1];
  const canRetry = !!error && error.status !== 402 && error.status !== 422 && lastMessage?.role === 'user';
  const empty = messages.length === 0;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: {
          xs: 'calc(100vh - var(--imp-banner-height, 0px) - 152px)',
          sm: 'calc(100vh - var(--imp-banner-height, 0px) - 160px)',
        },
        minHeight: 480,
        maxWidth: 920,
        mx: 'auto',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
          <SparkleIcon />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            Build Guide
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Describe what you want to do. The guide will suggest what to build and where to start.
          </Typography>
        </Box>
        {!empty && (
          <Button size="small" variant="outlined" startIcon={<RestartIcon />} onClick={startOver}>
            Start over
          </Button>
        )}
      </Stack>

      <Paper
        variant="outlined"
        sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <Box ref={scrollRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: { xs: 2, sm: 3 } }}>
          {empty ? (
            <Box sx={{ maxWidth: 640, mx: 'auto', textAlign: 'center', py: { xs: 2, sm: 6 } }}>
              <SparkleIcon color="primary" sx={{ fontSize: 44, mb: 1.5 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                What would you like AI to do for you?
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Tell me about the task in your own words. No technical knowledge needed. I'll ask a couple of
                questions, recommend the right EasyAI setup and walk you through building it.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                {STARTERS.map((s) => (
                  <Chip
                    key={s.label}
                    label={s.label}
                    variant="outlined"
                    color="primary"
                    onClick={() => send(s.message)}
                    disabled={sending || !loadedKey}
                    sx={{ fontSize: '0.875rem', height: 36, px: 0.5 }}
                  />
                ))}
              </Box>
            </Box>
          ) : (
            <Stack spacing={2.5}>
              {messages.map((m) => {
                const isUser = m.role === 'user';
                const isLast = m === lastMessage;
                return (
                  <Box
                    key={m.id}
                    sx={{ display: 'flex', gap: 1.5, flexDirection: isUser ? 'row-reverse' : 'row', alignItems: 'flex-start' }}
                  >
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: isUser ? 'grey.300' : 'primary.main',
                        color: isUser ? 'text.primary' : 'primary.contrastText',
                      }}
                    >
                      {isUser ? <PersonIcon fontSize="small" /> : <SparkleIcon fontSize="small" />}
                    </Avatar>
                    <Stack spacing={1.25} sx={{ minWidth: 0, maxWidth: isUser ? '80%' : 'calc(100% - 44px)', flex: isUser ? undefined : 1 }}>
                      <Box
                        sx={{
                          px: 2,
                          py: 1.25,
                          borderRadius: 2,
                          bgcolor: isUser ? 'primary.main' : 'action.hover',
                          color: isUser ? 'primary.contrastText' : 'text.primary',
                          alignSelf: isUser ? 'flex-end' : 'flex-start',
                          maxWidth: '100%',
                        }}
                      >
                        {isUser ? (
                          <Typography sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.95rem' }}>
                            {m.content}
                          </Typography>
                        ) : (
                          <Box sx={markdownSx}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]} skipHtml components={markdownComponents}>
                              {m.content}
                            </ReactMarkdown>
                          </Box>
                        )}
                      </Box>

                      {!isUser && m.recommendation && <RecommendationCard recommendation={m.recommendation} />}

                      {!isUser && !!m.actions?.length && (
                        <Stack spacing={1}>
                          {m.actions.map((a, i) => {
                            const primary = i === 0 && !!m.recommendation;
                            return (
                            <Button
                              key={a.path}
                              variant={primary ? 'contained' : 'outlined'}
                              disableElevation
                              onClick={() => {
                                trackClick(a.path);
                                navigate(a.path);
                              }}
                              endIcon={<ArrowForwardIcon />}
                              sx={{ justifyContent: 'space-between', textAlign: 'left', textTransform: 'none', py: 1, px: 2 }}
                            >
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {a.label}
                                </Typography>
                                {a.description && (
                                  <Typography
                                    variant="caption"
                                    sx={{ display: 'block', color: primary ? 'inherit' : 'text.secondary', opacity: primary ? 0.85 : 1 }}
                                  >
                                    {a.description}
                                  </Typography>
                                )}
                              </Box>
                            </Button>
                            );
                          })}
                        </Stack>
                      )}

                      {!isUser && isLast && !sending && !!m.quickReplies?.length && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {m.quickReplies.map((q) => (
                            <Chip key={q} label={q} variant="outlined" color="primary" onClick={() => send(q)} />
                          ))}
                        </Box>
                      )}
                    </Stack>
                  </Box>
                );
              })}

              {sending && (
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                    <SparkleIcon fontSize="small" />
                  </Avatar>
                  <Box sx={{ borderRadius: 2, bgcolor: 'action.hover' }}>
                    <TypingIndicator />
                  </Box>
                </Box>
              )}

              {error && (
                <Alert
                  severity="error"
                  action={
                    error.status === 402 ? (
                      <Button color="inherit" size="small" onClick={() => navigate('/dashboard/subscription')}>
                        Manage plan
                      </Button>
                    ) : canRetry ? (
                      <Button color="inherit" size="small" onClick={retry}>
                        Retry
                      </Button>
                    ) : undefined
                  }
                >
                  {error.message}
                </Alert>
              )}
            </Stack>
          )}
        </Box>

        <Box sx={{ borderTop: 1, borderColor: 'divider', p: { xs: 1.5, sm: 2 }, display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            maxRows={6}
            size="small"
            placeholder={empty ? 'Describe what you want to build…' : 'Reply to the guide…'}
            value={input}
            onChange={(e) => setInput(e.target.value.slice(0, MAX_MESSAGE_CHARS))}
            onKeyDown={handleKeyDown}
            disabled={sending || !loadedKey}
            inputProps={{ 'aria-label': 'Message the Build Guide', maxLength: MAX_MESSAGE_CHARS }}
            helperText={input.length > MAX_MESSAGE_CHARS - 200 ? `${input.length}/${MAX_MESSAGE_CHARS}` : 'Enter to send, Shift+Enter for a new line'}
          />
          <Tooltip title="Send">
            <span>
              <IconButton
                color="primary"
                onClick={() => send(input)}
                disabled={sending || !input.trim() || !loadedKey}
                aria-label="Send"
                sx={{ mb: 2.75 }}
              >
                <SendIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Paper>
    </Box>
  );
};

export default BuildGuide;
